import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import { newsCategories, newsList } from "@/mocks/news";
import { usePageTitle } from "@/hooks/usePageMeta";

const PAGE_SIZE = 20;

export default function NewsPage() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  usePageTitle(
    "影视资讯 - CC视频",
    "CC视频影视资讯频道，汇集业内最新动态、深度影评与电影节消息，了解影视行业正在发生什么。",
  );

  const filtered = useMemo(
    () => (category === "全部" ? newsList : newsList.filter((n) => n.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20editorial%20background%20of%20stacked%20newspapers%20and%20film%20stills%20on%20a%20deep%20navy%20desk%20with%20warm%20orange%20desk%20lamp%20glow%2C%20moody%20journalistic%20atmosphere%2C%20minimal%20cinematic%20texture%2C%20high%20contrast&width=1600&height=400&seq=cc-news-band&orientation=landscape"
          alt="影视资讯背景"
          title="CC视频影视资讯"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-transparent" />
        <div className="relative z-10 w-full px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-accent-400">影视资讯</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-background-50 tracking-tight">
            影视资讯
          </h1>
          <p className="mt-3 text-sm text-background-300 leading-relaxed max-w-2xl">
            当前资讯内容集中于业内最新动态、深度影评与电影节消息，用编辑视角陪你理解每一部作品背后的行业。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
            {newsCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setCategory(c);
                  setPage(1);
                }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  category === c
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <p className="text-xs text-foreground-500">
            共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span>{" "}
            篇资讯
          </p>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="暂无最新资讯"
            description="该分类下暂时还没有内容，看看其他分类吧。"
            actionLabel="查看全部资讯"
            onAction={() => {
              setCategory("全部");
              setPage(1);
            }}
          />
        ) : (
          <>
            <ul className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              {paged.map((n) => (
                <li key={n.id}>
                  <Link
                    to={`/news/detail/${n.id}`}
                    className="group flex flex-col sm:flex-row gap-3 md:gap-6 p-4 md:p-6 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-full sm:w-56 md:w-64 shrink-0 aspect-[16/10] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={n.cover}
                        alt={n.title}
                        title={`${n.title} - CC视频资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                        <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 font-medium">
                          {n.category}
                        </span>
                        <span className="font-label">{n.publishedAt}</span>
                        <span className="hidden sm:inline">来源：{n.source}</span>
                      </div>
                      <h2 className="mt-2 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-accent-600 transition-colors">
                        {n.title}
                      </h2>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2-custom">
                        {n.summary}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs text-foreground-700">
                        阅读全文
                        <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {totalPages > 1 && (
              <div className="mt-8 flex flex-col items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={current === 1}
                    onClick={() => setPage(current - 1)}
                    className="w-9 h-9 rounded-md border border-background-200 text-foreground-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-secondary-400 transition-colors cursor-pointer"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 rounded-md text-xs font-label transition-colors cursor-pointer ${
                        current === i + 1
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-200 text-foreground-600 hover:border-secondary-400"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={current === totalPages}
                    onClick={() => setPage(current + 1)}
                    className="w-9 h-9 rounded-md border border-background-200 text-foreground-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:border-secondary-400 transition-colors cursor-pointer"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
                  </button>
                </div>
                <p className="text-xs text-foreground-400 font-label">
                  第 {current} / {totalPages} 页
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}